import resolveUserSteamId from "./resolveUserSteamId.js";
import {
  getMembershipTier,
  normalizeMembershipTier,
} from "./getMembershipTierDisplay.js";

export { getMembershipTier, normalizeMembershipTier };

export const QUICK_FILTERS = [
  { id: "pending_review", label: "Pending review" },
  { id: "pending", label: "Pending" },
  { id: "paid", label: "Paid" },
  { id: "verified_free", label: "Verified Free" },
  { id: "rejected_evidence", label: "Rejected Evidence" },
  { id: "banned", label: "Banned" },
  { id: "missing_steam_id", label: "Missing Steam ID" },
];

export const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "verified", label: "Verified" },
  { value: "pending", label: "Pending" },
  { value: "pending_review", label: "Pending review" },
  { value: "rejected", label: "Rejected" },
  { value: "noteligible", label: "Not Eligible" },
  { value: "banned", label: "Banned" },
];

export const MEMBERSHIP_TIER_FILTER_OPTIONS = [
  { value: "all", label: "All memberships" },
  { value: "free", label: "Free" },
  { value: "premium", label: "Premium" },
  { value: "pro", label: "Pro" },
  { value: "unknown", label: "Unknown" },
];

export const VERIFICATION_STATE_FILTER_OPTIONS = [
  { value: "all", label: "All verification states" },
  { value: "pending_review", label: "Pending review" },
  { value: "legacy_evidence_review", label: "Legacy: evidence ready for review" },
  { value: "passport_rejected", label: "Passport rejected" },
  { value: "selfie_rejected", label: "Selfie rejected" },
  { value: "partial_evidence", label: "Legacy: partial evidence" },
  { value: "missing_evidence", label: "Legacy: missing evidence" },
  { value: "verified", label: "Verified" },
  { value: "rejected", label: "Rejected" },
  { value: "not_eligible", label: "Not Eligible" },
  { value: "banned", label: "Banned" },
];

export const PHONE_VERIFICATION_FILTER_OPTIONS = [
  { value: "all", label: "All phone states" },
  { value: "missing_phone", label: "Missing or invalid phone" },
  { value: "phone_not_started", label: "Phone not verified yet" },
  { value: "phone_code_sent", label: "Code sent" },
  { value: "phone_expired", label: "Code expired" },
  { value: "phone_verified", label: "Phone verified" },
];

export const MEMBERSHIP_SOURCE_FILTER_OPTIONS = [
  { value: "all", label: "All membership sources" },
  { value: "admin_granted", label: "Admin Granted" },
  { value: "self_serve_or_none", label: "Self-Serve or None" },
];

export const DEFAULT_USER_MANAGEMENT_FILTERS = {
  search: "",
  quickFilter: "",
  status: "all",
  membershipTier: "all",
  verificationState: "all",
  phoneVerificationState: "all",
  membershipSource: "all",
};

/**
 * Admin row helper: passport/selfie evidence (legacy flows only).
 * @param {object} row
 */
export function getEvidenceState(row) {
  const hasPassport = Boolean(row?.hasPassport || row?.passport?.file);
  const hasSelfieVideo = Boolean(row?.hasSelfieVideo || row?.selfie_video?.file);

  if (hasPassport && hasSelfieVideo) {
    return "both_uploaded";
  }

  if (!hasPassport && !hasSelfieVideo) {
    return "missing_both";
  }

  if (!hasPassport) {
    return "missing_passport";
  }

  return "missing_selfie";
}

function isRowPhoneNumberPlausible(row) {
  const phone =
    typeof row?.phoneNumber === "string" ? row.phoneNumber.trim() : "";
  if (!phone.startsWith("+")) {
    return false;
  }

  if (!/^\+[0-9().\s-]+$/.test(phone)) {
    return false;
  }

  const digits = phone.slice(1).replace(/\D/g, "");
  if (digits.length < 8 || digits.length > 15) {
    return false;
  }

  return true;
}

/**
 * @param {object} row
 * @returns {string}
 */
export function getPhoneRowFilterState(row) {
  if (!isRowPhoneNumberPlausible(row)) {
    return "missing_phone";
  }

  const st = row?.phoneVerification?.status;
  if (st === "verified") {
    return "phone_verified";
  }
  if (st === "code_sent") {
    return "phone_code_sent";
  }
  if (st === "expired") {
    return "phone_expired";
  }

  return "phone_not_started";
}

export function getVerificationState(row) {
  if (row?.accountStatus === "banned") {
    return "banned";
  }

  if (row?.accountStatus === "noteligible") {
    return "not_eligible";
  }

  if (row?.accountStatus === "verified") {
    return "verified";
  }

  if (row?.passportRejected) {
    return "passport_rejected";
  }

  if (row?.selfieVideoRejected) {
    return "selfie_rejected";
  }

  if (row?.accountStatus === "rejected") {
    return "rejected";
  }

  if (row?.accountStatus === "pending_review") {
    return "pending_review";
  }

  const evidenceState = getEvidenceState(row);

  if (row?.accountStatus === "pending" && evidenceState === "both_uploaded") {
    return "legacy_evidence_review";
  }

  if (evidenceState === "missing_both") {
    return "missing_evidence";
  }

  if (evidenceState === "missing_passport" || evidenceState === "missing_selfie") {
    return "partial_evidence";
  }

  return "pending";
}

export function matchesQuickFilter(row, quickFilterId) {
  if (!quickFilterId) {
    return true;
  }

  const membershipTier = getMembershipTier(row);
  const verificationState = getVerificationState(row);

  switch (quickFilterId) {
    case "pending_review":
      return row?.accountStatus === "pending_review";
    case "pending":
      return row?.accountStatus === "pending";
    case "paid":
      return membershipTier === "premium" || membershipTier === "pro";
    case "verified_free":
      return row?.accountStatus === "verified" && membershipTier === "free";
    case "rejected_evidence":
      return Boolean(
        row?.passportRejected ||
        row?.selfieVideoRejected ||
        row?.accountStatus === "rejected"
      );
    case "banned":
      return row?.accountStatus === "banned";
    case "missing_steam_id":
      return !resolveUserSteamId(row);
    default:
      return true;
  }
}

function getMembershipSource(row) {
  return getMembershipTier(row) !== "unknown" && row?.membership?.grantedByAdmin
    ? "admin_granted"
    : "self_serve_or_none";
}

export function rowMatchesFilters(row, filters = {}) {
  const search = String(filters.search || "").trim().toLowerCase();
  const membershipTier = getMembershipTier(row);
  const verificationState = getVerificationState(row);
  const phoneRowState = getPhoneRowFilterState(row);
  const membershipSource = getMembershipSource(row);

  const searchMatch =
    !search ||
    [row?.name, row?.email, row?.nickname, resolveUserSteamId(row)]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(search));

  const statusMatch =
    !filters.status ||
    filters.status === "all" ||
    row?.accountStatus === filters.status;

  const membershipTierMatch =
    !filters.membershipTier ||
    filters.membershipTier === "all" ||
    membershipTier === filters.membershipTier;

  const verificationStateMatch =
    !filters.verificationState ||
    filters.verificationState === "all" ||
    verificationState === filters.verificationState;

  const phoneVerificationStateMatch =
    !filters.phoneVerificationState ||
    filters.phoneVerificationState === "all" ||
    phoneRowState === filters.phoneVerificationState;

  const membershipSourceMatch =
    !filters.membershipSource ||
    filters.membershipSource === "all" ||
    membershipSource === filters.membershipSource;

  return (
    searchMatch &&
    statusMatch &&
    membershipTierMatch &&
    verificationStateMatch &&
    phoneVerificationStateMatch &&
    membershipSourceMatch &&
    matchesQuickFilter(row, filters.quickFilter)
  );
}
